import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { FormProvider, useWatch, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'


import FileInputUploadField from 'components/styledFormElements/FileInputUploadField'
import { SmoothTransition } from 'components/pageTransition'
import { useNotificationBar } from 'context/notificationContext'
import { getErrorResponse } from 'services/util'
import { useAddCategory, useCategoryContent, useUpdateCategoriesData } from 'hooks/category-manage'
import { Loader } from 'components/loader'
import { useGetTemplatesData } from 'hooks/templates'
import FormSelect from 'components/forms/FormSelect'
import { EpisodeForm } from 'components/categoryForm'
import Tooltip from 'components/styledFormElements/Tooltip'
import { categorySchema } from './validation'
import { FileSizeLimit } from 'constants/file-validation'
import { getImagePreviewUrl, normalizeLabel, parseLabels } from './helper'
import LabelsList from 'components/label/LabelList'
import InputField from 'components/styledFormElements/InputField'
import { ConfirmModal } from 'components/confirmationPopup'
import IconDropdown from 'components/styledFormElements/IconDropdown'
import { useGetIcons } from 'hooks/useIconMaster'
import { getImageSrc } from 'services/util'
import { Controller } from 'react-hook-form'
import { X } from 'lucide-react'
import { normalizeIconPath } from '../../../../src/services/util';
import { useActiveAIPrompt } from 'hooks/ai-settings'
import { useGenerateLabelsFromText } from 'hooks/ai-label-generation'
import { SparklesIcon } from '@heroicons/react/24/outline'

// -------------------------------------------------------
// Types
// -------------------------------------------------------
export interface ShowForm {
    name: string
    labels: string[]
    shortDescription: string
    longDescription: string
    publishStatus: string
    categoryPriority: number | null
    templateId: string
    icon: File | string | null
    bannerImage: File | string | null
    chipImage: File | string | null
    status: boolean
    collections: Options[]
    episodes: Options[]
    shows: Options[]
    entities: any
}

interface Options {
    label: string
    value: number
    avatar?: string
}

interface ImageDataUrls {
    iconSvgFile: string | null
    iconSvgPreview: string | null
    existingIconSvg: null
    chipImageFile: string | null
    chipImagePreview: string | null
    existingChipImage: null
    bannerImageFile: string | null
    bannerImagePreview: string | null
    existingBanner: null
}

// -------------------------------------------------------
// Constants
// -------------------------------------------------------
const DEFAULT_FORM_VALUES: ShowForm = {
    name: '',
    labels: [],
    bannerImage: '',
    shortDescription: '',
    longDescription: '',
    publishStatus: 'UNPUBLISHED',
    templateId: '0',
    categoryPriority: null,
    icon: null,
    chipImage: null,
    status: true,
    collections: [],
    episodes: [],
    shows: [],
    entities: [],
}

const DEFAULT_IMAGE_DATA: ImageDataUrls = {
    iconSvgFile: null,
    iconSvgPreview: null,
    existingIconSvg: null,
    chipImageFile: null,
    chipImagePreview: null,
    existingChipImage: null,
    bannerImageFile: null,
    bannerImagePreview: null,
    existingBanner: null,
}

// -------------------------------------------------------
// Main Component
// -------------------------------------------------------
export const AddUpdateCollection = () => {
    const params = useParams()
    const navigate = useNavigate()
    const notification = useNotificationBar()

    const methods = useForm<ShowForm>({
        defaultValues: DEFAULT_FORM_VALUES,
        resolver: yupResolver(categorySchema),
    })

    const { data: iconsResponse } = useGetIcons();

    const icons =
        Array.isArray(iconsResponse?.data?.results)
            ? iconsResponse.data.results
            : [];

    const [iconName, setIconName] = useState('');

    // Form
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        setError,
        watch,
        control,
        formState: { errors, touchedFields },
    } = methods

    const [labelInput, setLabelInput] = useState('')
    const [imageDataUrls, setImageDataUrls] = useState<ImageDataUrls>(DEFAULT_IMAGE_DATA);
    const [open, setOpen] = useState(false)
    const handleCancel = useCallback(() => setOpen(true), [])
    // const [prefillEpisodes, setPrefillEpisodes] = useState<Options[]>([])
    // const [prefillShows, setPrefillshows] = useState<Options[]>([])
    // const [preFillCollections, setPrefillCollections] = useState<Options[]>([])
    const [allEntities, setAllEntities] = useState<any[]>([])
    const [episodeLabels, setEpisodeLabels] = useState<
        Record<string, string[]>
    >({})
    // Watched values
    const watchedThumbnail = watch('icon')
    const watchedBanner = watch('bannerImage')
    const watchedChip = watch('chipImage')
    const labelsArray = methods.watch('labels')
    const watchedName = watch('name')
    const watchedShortDescription = watch('shortDescription')

    // AI Hooks
    const { data: activePrompt } = useActiveAIPrompt()
    const generateLabelsFromText = useGenerateLabelsFromText()
    const [isGeneratingLabels, setIsGeneratingLabels] = useState(false)
    const [aiGeneratedLabels, setAiGeneratedLabels] = useState<string[]>([])
    const previousCategoryNameRef = useRef<string>('')

    // API Hooks
    const { data: categoryData, isFetching: loadingCategory } =
        useCategoryContent(params.id, { enabled: !!params.id })

    const { mutate: addCategory, isLoading: adding } = useAddCategory()
    const { mutate: updateCategory, isLoading: updating } =
        useUpdateCategoriesData()

    const loading = adding || updating

    let {
        data: templteData,
        isFetching: loadingTemplates,
        refetch,
        isError,
        isSuccess,
        error,
    } = useGetTemplatesData(
        {
            fields: [
                { value: 1, id: 'status' },
                { value: 'CATEGORY', id: 'template_type' },
            ],
        },
        {
            keepPreviousData: true,
        }
    )

    const handleLabelAdd = () => {
        const token = labelInput.trim()
        if (!token) return

        const formatted = normalizeLabel(token)
        const current = methods.getValues('labels')

        if (current.some(label => label.toLowerCase() === formatted.toLowerCase())) return

        const next = [...current, formatted]

        methods.setValue('labels', next, { shouldValidate: true })
        setLabelInput('')
    }


    const handleLabelRemove = (index: number) => {
        const current = methods.getValues('labels')
        const removedLabel = current[index]
  const categoryName = watchedName?.trim()
  
  // Prevent removing the category name label
  if (categoryName && normalizeLabel(categoryName) === removedLabel) {
    notification.push({
      title: 'Info',
      type: 'info',
      message: 'Category name label cannot be removed'
    })
    return
  }
  
  const next = current.filter((_, i) => i !== index)

  // Update AI labels state if an AI-generated label was removed
  if (removedLabel && aiGeneratedLabels.some(l => l.toLowerCase() === removedLabel.toLowerCase())) {
    setAiGeneratedLabels(aiGeneratedLabels.filter(label => label.toLowerCase() !== removedLabel.toLowerCase()))
  }

        methods.setValue('labels', next, { shouldValidate: true })
    }

const handleGenerateLabels = async () => {
  const title = watchedName
  const description = watchedShortDescription
  
  if (!title.trim()) {
    notification.push({
      title: 'Error',
      type: 'error',
      message: 'Category name is required for AI label generation'
    })
    return
  }

  if (!description || !description.trim()) {
    notification.push({
      title: 'Error',
      type: 'error',
      message: 'Short description is required for AI label generation'
    })
    return
  }

  setIsGeneratingLabels(true)
  
  try {
    if (!activePrompt) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: 'No active AI prompt found. Please configure one in Settings.'
      })
      return
    }

    const result = await generateLabelsFromText.mutateAsync({
      title,
      description,
      prompt_text: activePrompt.prompt
    })
    
    const labels = (result as any)?.data?.results?.labels || (result as any)?.results?.labels || (result as any)?.labels
    
    if (labels && Array.isArray(labels)) {
      const normalizedLabels = labels
        .filter((label: string) => label && label.trim())
        .map((label: string) => normalizeLabel(label))
        .filter((label: string, index: number, self: string[]) => 
          self.findIndex(l => l.toLowerCase() === label.toLowerCase()) === index // Remove duplicates
        )

      // Preserve category name label
      const categoryName = watchedName?.trim()
      const categoryNameLabel = categoryName ? normalizeLabel(categoryName) : ''
      
      // Remove category name from AI labels if it exists (to avoid duplicate) - case insensitive
      const aiLabelsWithoutCategoryName = normalizedLabels.filter(label => 
        label.toLowerCase() !== categoryNameLabel.toLowerCase()
      )
      
      // Store AI-generated labels separately
      const finalAiLabels = categoryNameLabel 
        ? [categoryNameLabel, ...aiLabelsWithoutCategoryName]
        : aiLabelsWithoutCategoryName

      setAiGeneratedLabels(finalAiLabels)

      // Get current labels and separate non-AI labels (manual labels)
      const currentLabels = methods.getValues('labels') || []
      const nonAiLabels = currentLabels.filter(
        (label) => !aiGeneratedLabels.some(ai => ai.toLowerCase() === label.toLowerCase())
      )

      // Combine: non-AI labels + new AI labels
      const combinedLabels = [
        ...nonAiLabels,
        ...finalAiLabels.filter((l) => !nonAiLabels.some(n => n.toLowerCase() === l.toLowerCase()))
      ]

      setValue('labels', combinedLabels, { shouldValidate: true })

      notification.push({
        title: 'Success',
        type: 'success',
        message: result?.data?.message || 'Labels generated successfully'
      })
    }
  } catch (error) {
    notification.push({
      title: 'Error',
      type: 'error',
      message: 'Failed to generate labels. Please try again.'
    })
  } finally {
    setIsGeneratingLabels(false)
  }
}

  // Auto-add category name as label
  useEffect(() => {
    const categoryName = watchedName?.trim()
    const normalizedName = categoryName ? normalizeLabel(categoryName) : ''
    const previousNormalizedName = previousCategoryNameRef.current
    
    // Only update if the normalized name has actually changed
    if (normalizedName !== previousNormalizedName) {
      const currentLabels = methods.getValues('labels') || []
      
      // Remove the old category name label if it exists
      const withoutOldName = previousNormalizedName 
        ? currentLabels.filter(label => label !== previousNormalizedName)
        : currentLabels
      
      // Add the new category name label at the beginning (if not empty and not already present) - case insensitive check
      const updatedLabels = normalizedName 
        ? (withoutOldName.some(label => label.toLowerCase() === normalizedName.toLowerCase())
            ? withoutOldName 
            : [normalizedName, ...withoutOldName])
        : withoutOldName
      
      methods.setValue('labels', updatedLabels, { shouldValidate: true })
      
      // Update the ref to track the current category name
      previousCategoryNameRef.current = normalizedName
    }
  }, [watchedName, methods])

  // Remove duplicate labels whenever labels change
  useEffect(() => {
    if (!labelsArray || labelsArray.length === 0) return
    
    // Case-insensitive duplicate removal
    const uniqueLabels = labelsArray.filter((label: string, index: number, self: string[]) => 
      self.findIndex(l => l.toLowerCase() === label.toLowerCase()) === index
    )
    
    // Only update if duplicates were found
    if (uniqueLabels.length !== labelsArray.length) {
      methods.setValue('labels', uniqueLabels, { shouldValidate: true })
    }
  }, [labelsArray, methods])

    const handleImageSelect = useCallback(
        (
            field: keyof ShowForm,
            file: any,
            previewField: keyof ImageDataUrls,
            existingField: keyof ImageDataUrls
        ) => {
            if (!file) {
                setValue(field, null, { shouldDirty: true, shouldValidate: true })
                clearErrors(field)

                setImageDataUrls((prev) => ({
                    ...prev,
                    [previewField]: null,
                    [existingField]: null,
                }))

                return
            }

            setValue(field, file)
            clearErrors(field)

            const previewUrl = URL.createObjectURL(file)
            setImageDataUrls((prev) => ({
                ...prev,
                [previewField]: previewUrl,
            }))

            // Cleanup function for the preview URL
            return () => URL.revokeObjectURL(previewUrl)
        },
        [setValue, clearErrors]
    )


    // -------------------------------------------------------
    // Effects
    // -------------------------------------------------------
    // Prefill form when editing
    useEffect(() => {
        if (!params.id || !categoryData?.data?.category) return
        const { category } = categoryData.data
        const labels = parseLabels(category.labels)

        const collectionOptions: Options[] = category?.collections?.map(
            (e: any) => ({
                label: e.collection?.name,
                value: e.collection?.id,
                avatar: e.collection?.thumbnail,
            })
        )

        const showOptions: Options[] = category?.shows?.map((e: any) => ({
            label: e.show?.name,
            value: e.show?.id,
            avatar: e.show?.thumbnail,
        }))

        // Prepare episode options
        const episodeOptions: Options[] = category?.episodes?.map((e: any) => ({
            label: e.episode?.name,
            value: e.episode?.id,
            avatar: e.episode?.thumbnail,
        }))

        const allEntities = category?.entities?.map((e: any) =>
            e?.type === 'show'
                ? {
                    name: e.show?.name,
                    value: e.show?.id,
                    thumbnail: e.show?.thumbnail,
                    id: e.show?.id,
                    labels: e.show?.labels,
                    type: 'show',
                }
                : e?.type === 'collection'
                    ? {
                        name: e.collection?.name,
                        value: e.collection?.id,
                        thumbnail: e.collection?.thumbnail,
                        id: e.collection?.id,
                        labels: e.collection?.labels,
                        type: 'collection',
                    }
                    : {
                        name: e.episode?.name,
                        value: e.episode?.id,
                        thumbnail: e.episode?.thumbnail,
                        id: e.episode?.id,
                        labels: e.episode?.labels,
                        type: 'episode',
                    }
        )

        // Reset form with fetched data
        reset({
            name: category?.name,
            labels: labels,
            shortDescription: category.shortDescription,
            longDescription: category.longDescription,
            publishStatus: category.publishStatus,
            categoryPriority: category.categoryPriority,
            templateId: category.templateId,
            icon: category.icon,
            chipImage: category.chipImageUrl,
            bannerImage: category.bannerImage,
            status: !!category.status,
            episodes: episodeOptions,
            shows: showOptions,
            collections: collectionOptions,
            entities: allEntities,
        })

        // Update local state
        // setPrefillEpisodes(episodeOptions)
        // setPrefillshows(showOptions)
        // setPrefillCollections(collectionOptions)
        setAllEntities(allEntities)

        // Set existing image URLs
        setImageDataUrls((prev) => ({
            ...prev,
            existingChipImage: category.chipImageUrl,
            // existingIconSvg: category.icon,
            existingBanner: category.bannerImageUrl,
        }))


        const savedIconPath = normalizeIconPath(
            categoryData.data.category.icon
        );
        const savedIconName = categoryData.data.category.icon_name || '';

        if (savedIconPath) {
            setValue('icon', savedIconPath);
            setIconName(savedIconName);
        }
        
        // Initialize episodeLabels map for tracking entity-specific labels
        const initialEpisodeLabelsMap: Record<string, string[]> = {}
        allEntities?.forEach((entity: any) => {
            const labels = parseLabels(entity.labels)
            if (labels.length) {
                initialEpisodeLabelsMap[entity.id] = labels
            }
        })
        setEpisodeLabels(initialEpisodeLabelsMap)
        
        // Initialize previousCategoryNameRef to prevent duplicate label on edit
        previousCategoryNameRef.current = category.name ? normalizeLabel(category.name) : ''
    }, [categoryData, params?.id, setValue])

    // Image preview effects
    useEffect(() => {
        const previewUrl = getImagePreviewUrl(watchedThumbnail)
        setImageDataUrls((prev) => ({ ...prev, iconSvgPreview: previewUrl }))

        return () => {
            if (previewUrl && watchedThumbnail instanceof File) {
                URL.revokeObjectURL(previewUrl)
            }
        }
    }, [watchedThumbnail])

    useEffect(() => {
        const previewUrl = getImagePreviewUrl(watchedChip)
        setImageDataUrls((prev) => ({ ...prev, chipImagePreview: previewUrl }))

        return () => {
            if (previewUrl && watchedChip instanceof File) {
                URL.revokeObjectURL(previewUrl)
            }
        }
    }, [watchedChip])

    useEffect(() => {
        const previewUrl = getImagePreviewUrl(watchedBanner)
        setImageDataUrls((prev) => ({
            ...prev,
            bannerImagePreview: previewUrl,
        }))

        return () => {
            if (previewUrl && watchedBanner instanceof File) {
                URL.revokeObjectURL(previewUrl)
            }
        }
    }, [watchedBanner])

    // Cleanup image previews on unmount
    useEffect(() => {
        return () => {
            Object.values(imageDataUrls).forEach((url) => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url)
                }
            })
        }
    }, [])

    const rawListTemplates = useMemo(() => {
        if (!templteData?.data) return []
        return Array.isArray(templteData.data)
            ? templteData.data
            : templteData.data.results || []
    }, [templteData])

    const handleEpisodesChange = useCallback(
        (updatedEpisodes: any[]) => {
            const episodeLabelsMap: Record<string, string[]> = {}
            const allEpisodeLabels: string[] = []
            const currentEpisodeIds: string[] = []

            updatedEpisodes.forEach((ep: any) => {
                if (!ep) return

                const episodeId = ep.id
                currentEpisodeIds.push(episodeId)

                const labels = parseLabels(ep.labels)

                episodeLabelsMap[episodeId] = labels

                labels.forEach((l) => {
                    if (!allEpisodeLabels.some(existing => existing.toLowerCase() === l.toLowerCase())) {
                        allEpisodeLabels.push(l)
                    }
                })
            })

            // Find deleted episode labels
            const deletedEpisodeLabels: string[] = []

            Object.keys(episodeLabels).forEach((epId) => {
                if (!currentEpisodeIds.includes(epId)) {
                    episodeLabels[epId]?.forEach((label) => {
                        const stillUsed = Object.values(episodeLabelsMap).some(
                            (existing) => existing.some(l => l.toLowerCase() === label.toLowerCase())
                        )

                        if (!stillUsed && !deletedEpisodeLabels.some(l => l.toLowerCase() === label.toLowerCase())) {
                            deletedEpisodeLabels.push(label)
                        }
                    })
                }
            })

            setEpisodeLabels(episodeLabelsMap)

            // Manual labels from RHF
            const currentLabels = methods.getValues('labels') || []

            const manualLabels = currentLabels.filter(
                (label) =>
                    !deletedEpisodeLabels.some(d => d.toLowerCase() === label.toLowerCase()) &&
                    !allEpisodeLabels.some(e => e.toLowerCase() === label.toLowerCase())
            )

            const combinedLabels = manualLabels.concat(
                allEpisodeLabels.filter((l) => !manualLabels.some(m => m.toLowerCase() === l.toLowerCase()))
            )

            methods.setValue('labels', combinedLabels, {
                shouldValidate: true,
                shouldDirty: true,
            })
        },
        [episodeLabels, methods]
    )


    // -------------------------------------------------------
    // Submit Handler
    // -------------------------------------------------------
    const onSubmit = useCallback(
        async (values: ShowForm) => {
            const formData = new FormData()

            const collectionIds = values?.entities
                .filter((e: any) => e.type === 'collection')
                .map((e: any, i: number) => ({
                    id: e.id,
                    sortOrder: i + 1,
                }))

            const showIds = values?.entities
                .filter((e: any) => e.type === 'show')
                .map((e: any, i: number) => ({
                    id: e.id,
                    sortOrder: i + 1,
                }))

            // Extract episode IDs
            const episodeIds = values?.entities
                .filter((e: any) => e.type === 'episode')
                .map((e: any, i: number) => ({
                    id: e.id,
                    sortOrder: i + 1,
                }))

            const entities = values?.entities.map((e: any, i) => ({
                id: e.id,
                type: e.type,
                sortOrder: i + 1,
            }))

            formData.append(
                'contentData',
                JSON.stringify({
                    collections: collectionIds,
                    shows: showIds,
                    episodes: episodeIds,
                    entities: entities,
                })
            )

            // Basic fields
            formData.append('name', values.name)
            formData.append('shortDescription', values.shortDescription)
            formData.append('longDescription', values.longDescription)
            formData.append('templateId', values.templateId)
            formData.append('categoryPriority', String(values.categoryPriority))
            // Labels
            if (labelsArray.length) {
                formData.append('labels', JSON.stringify(labelsArray))
            }

            // Validate required images for new collections

            // Append image files if they exist
            if (values.chipImage instanceof File)
                formData.append('chipImage', values.chipImage)


            if (!values.icon) {
                return setError('icon', {
                    message: 'Icon is required',
                });
            }

            // ✅ Normalize before sending
            const cleanIconPath = typeof values.icon === 'string' ? normalizeIconPath(values.icon) : '';

            formData.append('icon', cleanIconPath);
            formData.append('icon_name', iconName);

            if (values.bannerImage instanceof File) {
                formData.append('bannerImage', values.bannerImage)
            }




            if (!values.chipImage && !imageDataUrls.existingChipImage) {
                return setError('chipImage', {
                    message: 'Thumbnail is required',
                })
            }

            const onError = (errorRes: any) => {
                notification.push(getErrorResponse(errorRes))
            }

            const onSuccess = () => {
                notification.push({
                    title: params.id ? 'Category Updated' : 'Category created',
                    type: 'success',
                    message: `${values?.name} ${params.id ? 'updated' : 'added'
                        } successfully`,
                })
                navigate(-1)
            }

            if (params.id) {
                updateCategory(
                    { id: params.id, data: formData },
                    { onSuccess, onError }
                )
            } else {
                addCategory({ data: formData }, { onSuccess, onError })
            }
        },
        [
            params.id,
            labelsArray,
            imageDataUrls,
            notification,
            navigate,
            addCategory,
            updateCategory,
            setError,
        ]
    )

    useEffect(() => {
        if (!watchedThumbnail || iconName) return;

        const match = icons.find((i: any) =>
            normalizeIconPath(i.icon || i.path || i.image) === watchedThumbnail
        );

        if (match?.name) {
            setIconName(match.name);
        }
    }, [watchedThumbnail, icons]);

    // -------------------------------------------------------
    // Render
    // -------------------------------------------------------
    return (
        <div className="w-full px-6 py-6 bg-gray-50 dark:bg-slate-900">
            {/* PAGE HEADER */}
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                    {params.id ? 'Edit Category' : 'Create Category'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Manage category details, images and linked content
                </p>
            </div>
            <ConfirmModal
                open={open}
                title="Are you sure?"
                message="This will remove all the data you have entered. This action cannot be undone."
                confirmText="Confirm"
                cancelText="Cancel"
                variant="danger"
                onCancel={() => setOpen(false)}
                onConfirm={() => {
                    setOpen(false)
                    navigate(-1)
                }}
            />

            {loadingCategory || loadingTemplates || loading ? (
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader />
                </div>
            ) : (
                <SmoothTransition>
                    <FormProvider {...methods}>
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="space-y-8"
                        >
                            {/* BASIC INFORMATION */}
                            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                                    Basic Information
                                </h4>

                                <div className="grid grid-cols-1 justify-center md:grid-cols-2 gap-4">

                                    <InputField
                                        label="Category Name"
                                        required
                                        helpText='Primary category label visible in navigation (e.g., Movies, Series, Kids). Used to structure content hierarchy on the platform.'
                                        placeholder="Enter Category Name"
                                        type="text"
                                        register={register("name", {
                                            required: 'Category name is required',
                                        })}
                                        
                                        error={errors?.name?.message}
                                    />


                                    <FormSelect
                                        label="Select Template"
                                        name="templateId"
                                        options={rawListTemplates}
                                        register={register('templateId', {
                                            required: 'Template is required',
                                            valueAsNumber: true,
                                        })}
                                        defaultOptionLabel="Default"
                                        errors={errors as any}
                                        helpText='Select a template to use for this category. If no template is selected, the default template will be used.'
                                    />

                                    <InputField
                                        label="Priority"
                                        required
                                        helpText='Display order of the category in the main navigation menu. Lower numbers appear first. Adjust to highlight featured categories.'
                                        placeholder="Enter Priority "
                                        type="number"
                                        register={register('categoryPriority', {
                                            required: 'Priority is required',
                                            valueAsNumber: true,
                                            min: {
                                                value: 1,
                                                message:
                                                    'Priority must be at least 1',
                                            },
                                        })}
                                        error={errors?.categoryPriority?.message}
                                    />

                                    {/* DESCRIPTIONS */}

                                    <InputField
                                        label="Short Description"
                                        required
                                        helpText='Brief overview of the category (min-15 characters and max 150 characters). Displayed in search results and category listings to attract viewers.
'
                                        placeholder="Enter Short Description "
                                        type="text"
                                        register={register('shortDescription', {
                                            required: 'Short Description is required',
                                        })}
                                        error={errors?.shortDescription?.message}
                                    />

                                    <InputField
                                        label="Long Description"
                                        helpText='Detailed description of the categories plot, characters, and themes. Displayed on the categories detail page for more context'
                                        placeholder="Enter Long Description "
                                        type="text"
                                        register={register('longDescription')}
                                        error={errors?.longDescription?.message}
                                        row={1}
                                        isTextarea={true}
                                    />
                                    {/* LABELS */}
                                    <div className="flex flex-col space-y-2">
                                        {/* Label + Tooltip */}
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                Labels
                                            </label>
                                            <span className="text-red-700">*</span>
                                            <Tooltip
                                                items={[
                                                    'Labels help organize and highlight content in this category.',
                                                    'Examples: Featured, Trending, New, Popular.',
                                                ]}
                                            />
                                        </div>

                                        {/* Input box (same as before) */}
                                        <input
                                            value={labelInput}
                                            onChange={(e) => setLabelInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault()
                                                    handleLabelAdd()
                                                }
                                            }}
                                            className="w-full rounded px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 dark:text-white text-sm"
                                            placeholder="Type label and press Enter"
                                        />

                            {/* AI Label Generation Button */}
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    title={!watchedName?.trim() ? "Category name is required" : !watchedShortDescription?.trim() ? "Short description is required" : "Generate labels using AI"}
                                    onClick={handleGenerateLabels}
                                    disabled={isGeneratingLabels || !watchedName?.trim() || !watchedShortDescription?.trim()}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                                >
                                    <SparklesIcon className="h-4 w-4" />
                                    {isGeneratingLabels ? 'Generating...' : 'Generate Labels'}
                                </button>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    AI will use name and short description
                                </span>
                            </div>

                                        {/* Labels container (textarea-like) */}
                                        <div className="border border-gray-300 dark:border-gray-600 rounded p-2 max-h-40 overflow-y-auto bg-gray-50 dark:bg-slate-800">
                                            {labelsArray?.length > 0 ? (
                                                <div className="grid grid-cols-1 md:grid-cols gap-2">
                                                    <LabelsList
                                                        labels={labelsArray}
                                                        onRemove={handleLabelRemove}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400">
                                                    No labels added
                                                </span>
                                            )}
                                        </div>

                                        {/* Error */}
                                        {errors?.labels && (
                                            <span className="text-xs text-red-500">
                                                {errors.labels.message}
                                            </span>
                                        )}
                                    </div>

                                </div>
                            </div>

                            {/* MEDIA + MAPPINGS (50 / 50) */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* CATEGORY IMAGES */}
                                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                                        Media Assets
                                    </h4>

                                    <div className="grid grid-cols-1 gap-6">
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                Icon <span className="text-red-700">*</span>
                                            </label>

                                            <Controller
                                                name="icon"
                                                control={control}
                                                render={({ field }) => {
                                                    const iconValue = field.value;

                                                    return (
                                                        <div className="mt-2">
                                                            {iconValue ? (
                                                                <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                                                                    <div className="flex items-center gap-2">
                                                                        <img
                                                                            src={
                                                                                typeof iconValue === 'string'
                                                                                    ? (iconValue.startsWith('http')
                                                                                        ? iconValue
                                                                                        : getImageSrc(iconValue))
                                                                                    : imageDataUrls.iconSvgPreview || undefined
                                                                            }
                                                                            alt="Selected icon"
                                                                            className="w-6 h-6 object-contain"
                                                                        />
                                                                        <span className="text-sm text-gray-700 dark:text-gray-200">
                                                                            {iconName ||
                                                                                (typeof iconValue === 'string' ? icons.find((i: any) =>
                                                                                    normalizeIconPath(i.icon || i.path || i.image) === iconValue
                                                                                )?.name : null) ||
                                                                                'Icon selected'}
                                                                        </span>
                                                                    </div>

                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            field.onChange('');
                                                                            setIconName('');
                                                                        }}
                                                                        className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                                                    >
                                                                        <X size={14} />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <IconDropdown
                                                                    onChange={(selectedIcon: any) => {
                                                                        const iconPath =
                                                                            selectedIcon?.icon ||
                                                                            selectedIcon?.path ||
                                                                            selectedIcon?.image ||
                                                                            '';

                                                                        if (!iconPath) return;

                                                                        field.onChange(iconPath);
                                                                        setIconName(selectedIcon?.name || '');
                                                                    }}
                                                                />
                                                            )}

                                                            {errors?.icon && (
                                                                <p className="text-xs text-red-600 mt-1">
                                                                    {errors.icon.message}
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                }}
                                            />
                                        </div>

                                        <FileInputUploadField
                                            label="Chip Image"
                                            fieldName="chipImage"
                                            register={register('chipImage', {
                                                required: 'Chip Image is required',
                                            }) as any}
                                            errors={errors}
                                            existingFile={
                                                imageDataUrls.existingChipImage
                                            }
                                            allowedTypes={FileSizeLimit?.chip_image?.allowedTypes}
                                            watchFile={watchedChip as any}
                                            onFileSelect={(file) =>
                                                handleImageSelect(
                                                    'chipImage',
                                                    file,
                                                    'chipImagePreview',
                                                    'existingChipImage'
                                                )
                                            }
                                            tooltip={FileSizeLimit?.chip_image?.tooltipItems as any}

                                            preview={
                                                imageDataUrls?.chipImagePreview
                                            }
                                            fileSizeLimit={FileSizeLimit?.chip_image}
                                            required
                                        />

                                        <FileInputUploadField
                                            label="Banner Image"
                                            fieldName="bannerImage"
                                            register={register('bannerImage') as any}
                                            errors={errors}
                                            existingFile={
                                                imageDataUrls.existingBanner
                                            }
                                            allowedTypes={FileSizeLimit?.banner_image?.allowedTypes}
                                            watchFile={watchedBanner as any}
                                            onFileSelect={(file) =>
                                                handleImageSelect(
                                                    'bannerImage',
                                                    file,
                                                    'bannerImagePreview',
                                                    'existingBanner'
                                                )
                                            }
                                            tooltip={FileSizeLimit?.banner_image?.tooltipItems as any}
                                            preview={
                                                imageDataUrls.bannerImagePreview
                                            }
                                            fileSizeLimit={FileSizeLimit?.banner_image}
                                        />
                                    </div>
                                </div>

                                {/* LINKED CONTENT */}
                                <div className="bg-white dark:bg-slate-900 border border-dashed border-gray-300 dark:border-slate-700 rounded-lg p-6">
                                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
                                        📦 Linked Content
                                    </h4>
                                    <p className="text-xs text-gray-500 mb-4">
                                        Map collections, shows and episodes to
                                        this category
                                    </p>

                                    <EpisodeForm
                                        initialData={allEntities}
                                        onEpisodesChange={handleEpisodesChange}
                                    />
                                </div>
                            </div>

                            {/* STICKY ACTION BAR */}
                            <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4 flex justify-end gap-3 z-10">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-6 py-2 bg-gray-300 dark:bg-slate-700 text-gray-800 dark:text-slate-200 rounded"
                                    disabled={loading}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {loading ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </FormProvider>
                </SmoothTransition>
            )}
        </div>
    )
}

export default AddUpdateCollection
